/******************************************************************************\
 * :$
 *
 * Copyright(c) 2022 SAS Institute Inc., Cary, NC, USA. All Rights Reserved.
 *
 * Name: useRequest
 *
 * Purpose:
 *
 * Author: craig (Craig.Simpson@sas.com)
 *
 * Support: SAS(r) Solutions OnDemand
 *
 * Input:
 *
 * Output:
 *
 * Parameters: (if applicable)
 *
 * Dependencies/Assumptions:
 *
 * Usage:
 *
 * History:
 * ddmmmyyyy userid description (Change Code)
 * 24Jun2022 craig File created.
 \******************************************************************************/
import { useState } from 'react';
import axios from 'axios';

type RequestConfig = {
    url?: string;
    baseURL?: string;
    initialIsLoading?: boolean;
    withCredentials?: boolean;
    onSuccess?: Function;
    onError?: Function;
    method?: any;
    data?: any;
};

type ResponseState = {
    data: any;
    isLoading: boolean | undefined;
    error: object | null;
    settings: object;
};

type RequestFunction = any;
type RequestResult = [ResponseState, RequestFunction];

const defaultConfig: RequestConfig = {
    url: '',
    method: 'get',
    initialIsLoading: false,
    withCredentials: true,
    baseURL: process.env.REACT_APP_API_URL,
    onSuccess: Function.prototype,
    onError: Function.prototype,
};

export const useRequest = (config: Readonly<RequestConfig>): RequestResult => {
    const settings = { ...defaultConfig, ...config };
    const {
        url,
        method,
        baseURL,
        initialIsLoading,
        onSuccess,
        onError,
        withCredentials,
    } = settings;

    const [isLoading, setIsLoading] = useState<boolean | undefined>(
        initialIsLoading,
    );

    const [error, setError] = useState<object | null>(null);
    const [data, setData] = useState<object | null>(null);

    const handleSuccessRequest = (serverData: any): void => {
        setIsLoading(false);

        if (serverData) {
            setError(null);
            setData(serverData);

            if (onSuccess) {
                onSuccess(serverData);
            }
        }
    };

    const handleErrorRequest = (serverError: object): void => {
        setIsLoading(false);

        if (serverError) {
            setError(serverError);

            if (onError) {
                onError(serverError);
            }
        }
    };

    const request = async (payload: any, options: object): Promise<any> => {
        try {
            setIsLoading(true);

            let requestConfig: RequestConfig = {
                baseURL,
                withCredentials,
                url,
                method,
            };

            if (payload) {
                requestConfig = { ...requestConfig, data: payload };
            }

            if (options) {
                requestConfig = { ...requestConfig, ...options };
            }

            const { data: serverData } = await axios(requestConfig);

            handleSuccessRequest(serverData);
        } catch (error) {
            if (axios.isCancel(error)) {
                if (error instanceof Error) {
                    console.error('Request canceled by axios', error.message);
                }
            } else {
                if (error instanceof Error) {
                    handleErrorRequest(error);
                }
            }
        }
    };



    const state: ResponseState = { data, isLoading, error, settings };

    return [state, request];
};

